/**
 * Loop Library Service
 *
 * Handles save / list / delete operations for the user's personal loop library.
 * All requests are sent to the Cloudflare Worker with credentials (session cookie).
 * Audio is never stored — only metadata needed to regenerate from Qdrant tokens.
 */

import type { SavedLoop } from '../generators/types';

// PROD: use relative /api path — see BACKEND_URL in app.config.ts for proxy rationale
const WORKER_BASE = import.meta.env.PROD
  ? '/api'
  : 'http://localhost:8787';

export interface SaveLoopPayload {
  album: string;
  genre: string;
  bpm: number;
  style_group: string;
  bar_size: string;
  bars: number;
  tokens: string[];
  durations: number[];
  token_types: string[];
  slots: Array<{ token: string; duration: number }>;
  total_duration: number;
  /** Custom pattern — only present when bar_size === 'CUSTOM' */
  custom_pattern?: Array<Record<string, number>>;
  /** Time signature numerator: 4 = 4/4, 3 = 3/4 or 6/8. Defaults to 4. */
  pulse_compas: number;
}

export class LoopLibraryService {
  /** In-memory cache of the user's saved loops (loop_id set for O(1) lookup) */
  private savedLoopIds: Set<string> = new Set();
  private loops: SavedLoop[] = [];

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  /**
   * Save a loop to the user's library.
   * Returns the deterministic loop_id assigned by the Worker.
   * Silently succeeds if the loop is already saved (INSERT OR IGNORE).
   */
  async saveLoop(payload: SaveLoopPayload): Promise<string> {
    const res = await fetch(`${WORKER_BASE}/loops/save`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (res.status === 429) {
      const data = await res.json() as { error?: string };
      // Use the server's message (tier-aware) if available, else a generic fallback
      throw new Error(data.error ?? 'Loop library full. Delete some loops to save new ones.');
    }
    if (!res.ok) {
      throw new Error(`Failed to save loop: ${res.status}`);
    }

    const data = (await res.json()) as { ok: boolean; loop_id: string };
    this.savedLoopIds.add(data.loop_id);
    return data.loop_id;
  }

  // ---------------------------------------------------------------------------
  // List
  // ---------------------------------------------------------------------------

  /**
   * Fetch the user's saved loops from the Worker (newest first).
   * Populates the in-memory cache used for "already saved" detection.
   */
  async listLoops(offset = 0): Promise<SavedLoop[]> {
    const res = await fetch(`${WORKER_BASE}/loops/list?offset=${offset}`, {
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch loops: ${res.status}`);
    }

    const data = (await res.json()) as { loops: SavedLoop[] };
    this.loops = data.loops;
    this.savedLoopIds = new Set(data.loops.map(l => l.loop_id));
    return data.loops;
  }

  // ---------------------------------------------------------------------------
  // Generate gate
  // ---------------------------------------------------------------------------

  /**
   * Call the Worker's generate gate before each loop generation.
   * - Authenticated free-tier users: enforces 10 generations/day cap.
   * - Authenticated pro users: records event, no cap.
   * - Unauthenticated users: returns 401; caller should proceed without gating.
   *
   * Returns { ok, remaining, limit } on success, throws on hard errors.
   * Returns null if the user is not authenticated (skip gate silently).
   */
  async checkGenerateGate(): Promise<{ ok: boolean; remaining: number | null; limit: number | null } | null> {
    const res = await fetch(`${WORKER_BASE}/loops/generate-gate`, {
      method: 'POST',
      credentials: 'include',
    });

    if (res.status === 401) {
      // Not logged in — no gate applies
      return null;
    }

    const data = await res.json() as {
      ok: boolean;
      error?: string;
      remaining: number | null;
      limit: number | null;
    };

    if (res.status === 429) {
      // Limit hit — caller should abort generation and show a message
      return { ok: false, remaining: 0, limit: data.limit };
    }

    return { ok: data.ok, remaining: data.remaining, limit: data.limit };
  }

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  /**
   * Delete a saved loop by loop_id.
   * The Worker enforces ownership — only the owning user can delete.
   */
  async deleteLoop(loopId: string): Promise<void> {
    const res = await fetch(`${WORKER_BASE}/loops/${loopId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Failed to delete loop: ${res.status}`);
    }

    this.savedLoopIds.delete(loopId);
    this.loops = this.loops.filter(l => l.loop_id !== loopId);
  }

  // ---------------------------------------------------------------------------
  // Local helpers
  // ---------------------------------------------------------------------------

  /** Returns true if the given loop_id is in the user's saved library (uses cache). */
  isSaved(loopId: string): boolean {
    return this.savedLoopIds.has(loopId);
  }

  /** Returns the cached list of saved loops (populated after listLoops()). */
  getCachedLoops(): SavedLoop[] {
    return this.loops;
  }

  /** Clear the in-memory cache (call on logout). */
  clearCache(): void {
    this.savedLoopIds.clear();
    this.loops = [];
  }
}

// ---------------------------------------------------------------------------
// Deterministic loop_id (mirrors Worker logic — same hash, same ID)
// ---------------------------------------------------------------------------

/**
 * Compute the deterministic loop_id for a given album + tokens + bpm + totalDuration.
 * Mirrors the makeLoopId() function in the Worker so the frontend can check
 * "is this loop already saved?" without a round-trip.
 * Including bpm and totalDuration prevents collisions between loops with the same
 * token sequence but different tempos or durations.
 */
export async function computeLoopId(
  album: string,
  tokens: string[],
  bpm: number,
  totalDuration: number,
  styleGroup: string,
  barSize: string
): Promise<string> {
  const input = `${album}:${bpm}:${styleGroup}:${barSize}:${tokens.join(',')}:${totalDuration.toFixed(3)}`;
  const data = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}
